/**
 * Privacy lifecycle: wipe transient PII (OCR scratch, caches) when the page is
 * hidden or unloaded. Faithful in intent to the monolith's wipeTransientData —
 * clears OCR canvas/worker, sessionStorage, transient IndexedDB, and non-app
 * Cache Storage, while preserving the app's localStorage and the SW/MGL caches.
 */

import { byId } from '@/ui/dom';
import { terminateOcr } from '@/ocr/ocr';

const TRANSIENT_DBS = ['spatchai_ocr_cache', 'keyval-store', 'tesseract_cache', 'tesseract-store'];

export async function wipeTransientData(): Promise<void> {
  try {
    await terminateOcr();
  } catch {
    /* worker already gone */
  }

  const canvas = byId('cam-canvas') as HTMLCanvasElement | null;
  if (canvas) {
    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    canvas.width = 0;
    canvas.height = 0;
  }

  try {
    sessionStorage.clear();
  } catch {
    /* unavailable */
  }

  for (const name of TRANSIENT_DBS) {
    try {
      indexedDB.deleteDatabase(name);
    } catch {
      /* best effort */
    }
  }

  try {
    if ('caches' in globalThis) {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter((k) => !k.startsWith('spatchai-') && !k.startsWith('mgl-data-'))
          .map((k) => caches.delete(k))
      );
    }
  } catch {
    /* best effort */
  }
}

/** Register the visibility/pagehide privacy hooks. */
export function installPrivacyHooks(): void {
  document.addEventListener('pagehide', () => void wipeTransientData());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void wipeTransientData();
  });
}
